// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {clampVoiceVolumePercent} from '@app/features/voice/utils/VoiceVolumeUtils';
import {STREAM_AUDIO_PREFS_PRUNE_INTERVAL_MS, STREAM_AUDIO_PREFS_TTL_MS} from '@app/vendor/constants/StreamConstants';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('StreamAudioPrefs');
interface StreamAudioPrefsEntry {
	volume: number;
	muted: boolean;
	lastAccessed: number;
}
class StreamAudioPrefs {
	entries: Record<string, StreamAudioPrefsEntry> = {};
	private pruneIntervalId: number | null = null;
	constructor() {
		makeAutoObservable<this, 'pruneIntervalId'>(
			this,
			{pruneIntervalId: false, hasEntry: false, getVolume: false, isMuted: false},
			{autoBind: true},
		);
		void this.initPersistence();
		this.startPruneInterval();
	}
	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'StreamAudioPrefs', ['entries']);
	}
	hasEntry(streamKey: string): boolean {
		return streamKey in this.entries;
	}
	getVolume(streamKey: string): number {
		return clampVoiceVolumePercent(this.entries[streamKey]?.volume ?? 100);
	}
	isMuted(streamKey: string): boolean {
		return this.entries[streamKey]?.muted ?? false;
	}
	setVolume(streamKey: string, volume: number): void {
		const clamped = clampVoiceVolumePercent(volume);
		const existing = this.entries[streamKey];
		if (existing && existing.volume === clamped) {
			this.touchStream(streamKey);
			return;
		}
		this.entries = {
			...this.entries,
			[streamKey]: {
				volume: clamped,
				muted: existing?.muted ?? false,
				lastAccessed: Date.now(),
			},
		};
		logger.debug('Set stream volume', {streamKey, volume: clamped});
	}
	setMuted(streamKey: string, muted: boolean): void {
		const existing = this.entries[streamKey];
		if (existing && existing.muted === muted) {
			this.touchStream(streamKey);
			return;
		}
		this.entries = {
			...this.entries,
			[streamKey]: {
				volume: existing?.volume ?? 100,
				muted,
				lastAccessed: Date.now(),
			},
		};
		logger.debug('Set stream mute', {streamKey, muted});
	}
	touchStream(streamKey: string): void {
		const existing = this.entries[streamKey];
		if (!existing) return;
		const now = Date.now();
		if (existing.lastAccessed === now) return;
		this.entries = {
			...this.entries,
			[streamKey]: {
				...existing,
				lastAccessed: now,
			},
		};
	}
	private startPruneInterval(): void {
		if (this.pruneIntervalId != null) {
			return;
		}
		this.pruneIntervalId = window.setInterval(() => {
			this.pruneExpiredEntries();
		}, STREAM_AUDIO_PREFS_PRUNE_INTERVAL_MS);
	}
	private pruneExpiredEntries(): void {
		const cutoff = Date.now() - STREAM_AUDIO_PREFS_TTL_MS;
		const nextEntries = {...this.entries};
		let removedCount = 0;
		for (const [streamKey, entry] of Object.entries(this.entries)) {
			if (entry.lastAccessed < cutoff) {
				delete nextEntries[streamKey];
				removedCount += 1;
			}
		}
		if (removedCount > 0) {
			this.entries = nextEntries;
			logger.debug('Pruned stream audio prefs', {removedCount});
		}
	}
}
export default new StreamAudioPrefs();
